import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShieldAlert, LogIn } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Appears globally when the backend returns 440 (SESSION_KICKED) — i.e., the
 * user just got signed out because another device signed in. Blocks the UI
 * until they acknowledge, then drops them to the login page.
 */
export default function SessionKickedModal() {
  const { sessionKicked, setSessionKicked } = useAuth();
  if (!sessionKicked) return null;

  const handleContinue = () => {
    setSessionKicked(false);
    // Cookies were already cleared server+client-side by the axios interceptor
    // when it caught the 440. Hard-navigate so every stale axios call + in-memory
    // state is reset cleanly — the login page then handles the fresh sign-in
    // flow including OTP if needed.
    window.location.href = "/login?kicked=1";
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{ background: "rgba(4,11,28,0.82)", backdropFilter: "blur(18px)" }}
        data-testid="session-kicked-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 260 }}
          className="relative w-full max-w-md rounded-[22px] overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #0b1d3c 0%, #081429 100%)",
            border: "1px solid rgba(244,63,94,0.32)",
            boxShadow: "0 28px 80px rgba(244,63,94,0.18), 0 0 0 1px rgba(255,255,255,0.03) inset",
          }}
        >
          {/* Cinematic ambient bloom */}
          <div aria-hidden className="absolute -top-20 -right-20 w-60 h-60 rounded-full pointer-events-none"
               style={{ background: "radial-gradient(circle, rgba(244,63,94,0.22), transparent 70%)", filter: "blur(30px)" }} />

          <div className="relative p-7">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                   style={{
                     background: "rgba(244,63,94,0.12)",
                     border: "1px solid rgba(244,63,94,0.32)",
                   }}>
                <ShieldAlert size={22} className="text-rose-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="lms-mono text-[10px] tracking-[0.18em] uppercase text-rose-300 mb-1">
                  — Signed out remotely
                </div>
                <h2 className="lms-display text-white text-[22px] leading-tight">
                  Your account is now active on another device.
                </h2>
              </div>
            </div>

            <p className="mt-4 text-[14px] leading-relaxed text-white/70">
              LogicsWallah enforces <strong className="text-white">single-device access</strong> to protect your content.
              Your session on this device has been signed out because your account just signed in elsewhere.
            </p>

            <div className="mt-5 rounded-[14px] px-4 py-3 text-[12.5px] leading-relaxed"
                 style={{ background: "rgba(244,63,94,0.06)", border: "1px solid rgba(244,63,94,0.20)", color: "#fecdd3" }}>
              <strong>If this wasn't you</strong>, someone else signed into your account. Log back in and immediately
              reset your password from <span className="font-mono">Settings → Security</span>.
            </div>

            <button
              type="button"
              onClick={handleContinue}
              className="mt-6 w-full h-12 rounded-full font-semibold inline-flex items-center justify-center gap-2 transition-all"
              style={{
                background: "linear-gradient(135deg, #5ceeff, #00bcd4)",
                color: "#04101f",
                boxShadow: "0 10px 28px rgba(92,238,255,0.25)",
              }}
              data-testid="session-kicked-continue"
            >
              <LogIn size={15} /> Take me to login
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
